import { api } from '../api';
import { can, type Me } from '../auth/me';

/**
 * Owner 2026-10-30 21:59: the scope items („Realizacja FOH”, „Technika”…) give their lines in the quote at once. A
 * scope line in the draft (`GET/POST /api/quotes/events/:id/scope-lines`): `in` — wanted by its scope; `kept` — its
 * scope went but it was changed by hand, so it stays. `covers`: the scope items it stands for, by the names their
 * lines would have (M-a). `name`/`covers` null: the lines' „Nazwa” is hidden (M1); `quantity` null: „Ilość” hidden.
 */
export type ScopeLine = { lineId: string; name: string | null; quantity: number | null; state: 'in' | 'kept'; covers: Array<string | null> };
/** The scope lines and the ids of every line of the draft (a line the quote had and the scope took over is not „dodano”, M2). */
export type ScopeReport = { lines: ScopeLine[]; lineIds: string[] };

/** 1 / 2–4 / 5+ (12–14: the third), as Polish counts. */
const form = (n: number, one: string, few: string, many: string) => (n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? few : many);
const named = (ls: ScopeLine[]) => ls.every((l) => l.name !== null);
const quoted = (ls: ScopeLine[]) => ls.map((l) => `„${l.name}”`).join(', ');
/** What was added or taken off: the names, or (M1) how many. */
const what = (ls: ScopeLine[]) => (named(ls) ? quoted(ls) : `${ls.length} ${form(ls.length, 'pozycję', 'pozycje', 'pozycji')} z zakresu prac`);

/** What saving the scope did to the quote, in one line; '' when nothing changed. */
export function scopeLinesNotice(before: ScopeReport, after: ScopeReport): string {
  const was = new Map(before.lines.map((l) => [l.lineId, l.state]));
  const existed = new Set(before.lineIds);
  const now = new Set(after.lineIds);
  const scoped = new Set(after.lines.map((l) => l.lineId));
  const fresh = after.lines.filter((l) => l.state === 'in' && was.get(l.lineId) !== 'in');
  const added = fresh.filter((l) => !existed.has(l.lineId) || was.has(l.lineId));
  const taken = fresh.filter((l) => existed.has(l.lineId) && !was.has(l.lineId));
  const removed = before.lines.filter((l) => l.state === 'in' && !now.has(l.lineId));
  const released = before.lines.filter((l) => l.state === 'in' && now.has(l.lineId) && !scoped.has(l.lineId));
  const kept = after.lines.filter((l) => l.state === 'kept' && was.get(l.lineId) === 'in');
  // M-a: scope items now covered by a line that was already the scope's (its spare quantity)
  const prior = new Map(before.lines.map((l) => [l.lineId, l]));
  const covered = after.lines.flatMap((l) => {
    const b = prior.get(l.lineId);
    if (!b || b.state !== 'in' || l.state !== 'in' || l.name === null) return [];
    const more = l.covers.filter((c): c is string => c !== null && !b.covers.includes(c));
    return more.length > 0 ? [`${more.map((c) => `„${c}”`).join(', ')} ${more.length === 1 ? 'pokryty' : 'pokryte'} istniejącą pozycją „${l.name}”${l.quantity !== null ? ` ×${String(l.quantity).replace('.', ',')}` : ''}`] : [];
  });
  const parts = [
    added.length > 0 ? `dodano ${what(added)}` : '',
    removed.length > 0 ? `zdjęto ${what(removed)}` : '',
    taken.length > 0 ? (named(taken) ? `przejęto ${form(taken.length, 'istniejącą', 'istniejące', 'istniejące')} ${quoted(taken)}` : `przejęto ${taken.length} ${form(taken.length, 'istniejącą pozycję', 'istniejące pozycje', 'istniejących pozycji')}`) : '',
    ...covered,
    // M-b: a line let go by the scope (taken over by hand, or its function changed) stays as an ordinary line
    released.length > 0 ? (named(released) ? `odłączono od zakresu ${quoted(released)} — ${form(released.length, 'zostaje', 'zostają', 'zostają')} w kosztorysie` : `odłączono od zakresu ${released.length} ${form(released.length, 'pozycję', 'pozycje', 'pozycji')} — ${form(released.length, 'zostaje', 'zostają', 'zostaje')} w kosztorysie`) : '',
  ].filter(Boolean);
  const head = parts.length > 0 ? `Kosztorys: ${parts.join('; ')}.` : '';
  const tail =
    kept.length === 0
      ? ''
      : !named(kept)
        ? `${form(kept.length, 'Zostaje', 'Zostają', 'Zostaje')} ${kept.length} ${form(kept.length, 'pozycja zmieniona', 'pozycje zmienione', 'pozycji zmienionych')} ręcznie.`
        : kept.length === 1
          ? `Zostaje ${quoted(kept)} — zmieniona ręcznie (usuń ją w kosztorysie, jeśli niepotrzebna).`
          : `Zostają ${quoted(kept)} — zmienione ręcznie (usuń je w kosztorysie, jeśli niepotrzebne).`;
  if (!head && !tail) return '';
  return [head || 'Kosztorys:', tail].filter(Boolean).join(' ');
}

/** Only whoever may edit quotes is told (the lines come anyway: the rules add them as the system). */
export const maySeeScopeLines = (me: Me | undefined): boolean => !!me && can(me, 'quotes.quotes', 'edit');

/** The scope rules' lines now (`sync`: after saving — the rules' lines brought in first); null when not to be told. */
export async function readScopeLines(eventId: string, sync: boolean): Promise<ScopeReport | null> {
  try {
    return await api<ScopeReport>(`/api/quotes/events/${eventId}/scope-lines`, sync ? { method: 'POST' } : {});
  } catch {
    // no right to this quote (scope „own”: someone else's), a network error: the event is saved, nothing to say
    return null;
  }
}
